import { v4 as uuidv4 } from 'uuid';
import { KeycloakProfile } from 'keycloak-js';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useState, useEffect, useRef } from 'react';

import {
  USER_PROFILE,
  CORRELATION_ID,
  OTP_PENALTY_TIME,
  NORTADA_LAST_USER,
  AUTHORIZATION_TOKEN,
  NORTADA_NEW_CLIENT_PROFILE_STEP
} from '../../constants/storage';
import endpoints from '../../api/endpoints';
import { nortadaLogger } from '../common/logger';
import { Privileges, CLIENT_STATUS, UserAccount } from '../common/client-status/interfaces';
import { isJsonResponse } from '../../constants/helpers/utils';
import { nortadaStorage } from '../../constants/helpers/storage';
import { PORTAL_PATH, PROFILE_CREATION_PATH, EXTERNAL_PATH, COMMON_PATH } from '../routes/paths';
import { apiCodeBusinessErrorsMapper } from '../../constants/errors';
import { fetchWithTokenRefresh } from '../../api/fetchWithTokenRefresh';

import { LogoutMessage, authentication } from './authentication';
import { AUTHENTICATION_CODE } from './consts';
import { getInitialUrl, isRefreshedAsLoggedUser } from './helpers';

import { isPortalRoute } from '../routes/portalConfig';
import { isProfileCreationRoute } from '../routes/profileCreationConfig';

export const useAuthentication = () => {
  const [auth, setAuth] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [privileges, setPrivileges] = useState<Privileges>({
    api: [],
    portal: []
  });
  const [userAccount, setUserAccount] = useState<UserAccount>({
    username: '',
    firstName: undefined,
    lastName: undefined
  });
  const [clientStatus, setClientStatus] = useState<CLIENT_STATUS>('CLIENT_PROFILE_PENDING');

  const history = useHistory();
  const location = useRef(useLocation());
  const redirect = useCallback(
    (url: string, message: LogoutMessage) => {
      history.push(url, { message });
    },
    [history]
  );

  const handleChangeAuthState = (isAuth: boolean) => {
    setAuth(isAuth);
  };

  useEffect(() => {
    authentication.initialize({
      redirect,
      handleChangeAuthState
    });
  }, [redirect]);

  function toUserAccount(profile: KeycloakProfile): UserAccount {
    return {
      username: profile.username || '',
      firstName: profile.firstName,
      lastName: profile.lastName
    };
  }

  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const profile = await authentication.userProfile();
        setUserAccount(toUserAccount(profile));
        return profile;
      } catch (e) {
        setLoading(false);
        throw new Error(e);
      }
    };

    const checkClientStatus = async (isKeycloakAuth: boolean, profile: KeycloakProfile) => {
      try {
        const response = await fetchWithTokenRefresh(endpoints.clientStatus);

        if (isJsonResponse(response)) {
          const jsonResponse = await response.json();
          if (response.status === 200) {
            switch (jsonResponse.status) {
              case AUTHENTICATION_CODE.CLIENT_PROFILE_ACTIVE: {
                const responsePrivileges: Privileges = {
                  api: jsonResponse.apiPrivileges || [],
                  portal: jsonResponse.portalPrivileges || []
                };

                setAuth(isKeycloakAuth);
                setPrivileges(responsePrivileges);
                setClientStatus(jsonResponse.status);
                setUserAccount(toUserAccount(profile));
                break;
              }
              case AUTHENTICATION_CODE.USER_INVITED:
                nortadaStorage.setItem(USER_PROFILE, profile);
                nortadaStorage.setItem(NORTADA_NEW_CLIENT_PROFILE_STEP, 0);
                setClientStatus(jsonResponse.status);
                setAuth(isKeycloakAuth);

                history.push(PROFILE_CREATION_PATH.USER_INVITED);
                break;
              case AUTHENTICATION_CODE.CLIENT_PROFILE_NOT_FOUND:
                nortadaStorage.setItem(USER_PROFILE, profile);
                nortadaStorage.setItem(NORTADA_NEW_CLIENT_PROFILE_STEP, 0);
                setClientStatus(jsonResponse.status);
                setAuth(isKeycloakAuth);

                history.push(PROFILE_CREATION_PATH.PROFILE_CREATION);
                break;
              case AUTHENTICATION_CODE.CLIENT_PROFILE_PENDING:
                nortadaStorage.setItem(NORTADA_NEW_CLIENT_PROFILE_STEP, 1);
                setClientStatus(jsonResponse.status);
                setAuth(isKeycloakAuth);

                history.push(PROFILE_CREATION_PATH.PROFILE_CREATION);
                break;
              case AUTHENTICATION_CODE.CLIENT_PROFILE_BLOCKED:
                nortadaStorage.setItem(OTP_PENALTY_TIME, jsonResponse.penaltyTime);
                setClientStatus(jsonResponse.status);
                setAuth(isKeycloakAuth);

                history.push(PROFILE_CREATION_PATH.PROFILE_CREATION_BLOCKED);
                break;
              default: {
                throw new Error(`${jsonResponse.status} is wrong client status`);
              }
            }
          }
        } else {
          const fetchResponse = await response.text();

          throw new Error(fetchResponse);
        }
      } catch (e) {
        throw new Error(e);
      }
    };

    const handleActiveSession = async () =>
      authentication.initKeycloakSession({
        onSuccess: async () => {
          const userProfile = await getUserProfile();
          await checkClientStatus(true, userProfile);
          setLoading(false);
          handleChangeAuthState(true);
        }
      });

    const setAuthToken = () => {
      const authToken = authentication.token;

      if (authToken) {
        localStorage.setItem(AUTHORIZATION_TOKEN, authToken);
      }

      localStorage.setItem(CORRELATION_ID, uuidv4());
    };

    const login = async () => {
      try {
        const isActiveSession = await handleActiveSession();
        if (isActiveSession) {
          return;
        }

        const isKeycloakAuth: boolean = await authentication.login();

        setAuthToken();

        const userProfile = await getUserProfile();
        const lastLoggedUser = sessionStorage.getItem(NORTADA_LAST_USER);
        const initialUrl = getInitialUrl();

        if (!lastLoggedUser) {
          sessionStorage.setItem(NORTADA_LAST_USER, userProfile?.username || '');
        }

        if (isRefreshedAsLoggedUser(initialUrl)) {
          if (lastLoggedUser !== userProfile?.username) {
            sessionStorage.setItem(NORTADA_LAST_USER, userProfile?.username || '');
            history.push(PORTAL_PATH.DASHBOARD);
          } else if (initialUrl?.includes(EXTERNAL_PATH.USSD_EDITOR)) {
            history.push(PORTAL_PATH.USSD_APPS);
          } else {
            history.push(initialUrl as string);
          }
        }
        await checkClientStatus(isKeycloakAuth, userProfile);
      } catch (e) {
        nortadaLogger.error(e.message);
        authentication.logout({ text: apiCodeBusinessErrorsMapper(e.message), severity: 'warning' });
      } finally {
        setLoading(false);
      }
    };

    if (
      location.current.pathname === COMMON_PATH.HOME ||
      isPortalRoute(location.current.pathname) ||
      isProfileCreationRoute(location.current.pathname)
    ) {
      login().finally(() => {});
    } else {
      setLoading(false);
    }
  }, [history]);

  return { auth, loading, privileges, clientStatus, userAccount };
};
